import React from 'react';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { useDispatch } from 'react-redux';
import AIDialog from './Dialogs/AIDialog';
import AgreementAcceptanceDialog, {
	isAgreementRequiredError,
} from '@/components/legal/AgreementAcceptanceDialog';
import { IconButton } from '@/components/ui/custom/IconButton';
import { Dialog } from '@/components/ui/dialog';
import {
	Zap,
	MessageCircleMore,
	Minimize2,
	Smile,
	Handshake,
	SpellCheck,
	Pencil
} from 'lucide-react';
import {
	TooltipTrigger,
	Tooltip,
	TooltipContent
} from '@/components/ui/tooltip';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
	addTypingIndicator,
	removeTypingIndicator
} from '../../../actions/typingIndicator';
import getWebchatUrl from '../../../utils/getWebchatUrl';
import { toast } from 'sonner';
import { toastPosition } from '@/components/notifications/toastPosition';
import { generatedResponseAction } from './aiDraft';

// The thread's typing bubble shows the AI's progress; the icon just fades while it works.
const AiZapIcon = ({ loading }) => (
	<Zap className={cn('size-4', loading && 'animate-pulse')} />
);

// create functional component
// On phones the composer's "+" sheet lists the actions and runs them through the
// ref (`run`, `openEnable`), with `hideTrigger` set so no button shows.
const AIEnhanceButton = React.forwardRef((props, ref) => {
	const dispatch = useDispatch();
	const [loading, setLoading] = React.useState(false);
	const [isTyping, setIsTyping] = React.useState(false);
	const [typingText, setTypingText] = React.useState('');
	const [aiEnableDialogOpen, setAiEnableDialogOpen] = React.useState(false);
	const [agreementDialogOpen, setAgreementDialogOpen] = React.useState(false);
	const [requiredAgreements, setRequiredAgreements] = React.useState([]);
	const pendingRetryRef = React.useRef(null);

	const webchatApiUrl = getWebchatUrl(window.location.href);

	// Send AI typing indicator to server for real-time updates
	const sendAITypingToServer = (isTyping, status = 'typing') => {
		if (!props.activeConversation?._id) {
			return;
		}

		try {
			axios
				.post(`${webchatApiUrl}/api/webchat/typing`, {
					isTyping: isTyping,
					whoIsTyping: 'AI Assistant',
					_customerConversation: props.activeConversation._id,
					aiStatus: status, // Include AI status for server
					at: Date.now()
				})
				.catch((err) => {
					console.log('Error sending AI typing indicator:', err);
				});
		} catch (err) {
			console.log('Error sending AI typing indicator:', err);
		}
	};

	// Use the existing typing indicator system
	const showAITypingIndicator = (status) => {
		if (!props.activeConversation?._id) {
			console.log('No active conversation ID');
			return;
		}

		// Always remove any existing AI typing indicators first
		const removeData = {
			whoIsTyping: 'AI Assistant',
			_customerConversation: props.activeConversation._id
		};
		dispatch(removeTypingIndicator(removeData));

		if (status === 'none') {
			// Send stop typing to server
			sendAITypingToServer(false);
			return; // Already removed above
		}

		const typingData = {
			whoIsTyping: 'AI Assistant',
			_customerConversation: props.activeConversation._id,
			aiStatus: status // Add custom property to track AI status
		};

		dispatch(addTypingIndicator(typingData));

		// Send start typing to server for real-time updates
		sendAITypingToServer(true, status);
	};

	// Notify parent component of typing state changes
	React.useEffect(() => {
		if (props.onTypingStateChange) {
			props.onTypingStateChange({ isTyping, typingText });
		}
	}, [isTyping, typingText, props.onTypingStateChange]);

	// In a conversation the thread's typing bubble already shows this.
	const showThinkingToast = () => {
		if (props.activeConversation?._id) return;
		toast.loading('AI is thinking...', {
			id: 'ai-enhance',
			position: toastPosition('bottom-right')
		});
	};

	const dismissThinkingToast = () => {
		toast.dismiss('ai-enhance');
	};

	// The AI's note for the team from Generate Text (what it could not answer or
	// what to check); never meant for the customer, so it is not typed in.
	const showStaffNote = (title, note) => {
		toast.warning(title, {
			description: note,
			duration: 12000,
			position: toastPosition('bottom-right')
		});
	};

	const openAgreementDialog = (agreements, retryFn) => {
		pendingRetryRef.current = retryFn;
		setRequiredAgreements(agreements);
		setAgreementDialogOpen(true);
		setLoading(false);
		setIsTyping(false);
		showAITypingIndicator('none');
		dismissThinkingToast();
	};

	const handleAgreementError = (err, retryFn) => {
		const agreementError = isAgreementRequiredError(err);
		if (agreementError) {
			openAgreementDialog(agreementError.requiredAgreements, retryFn);
			return true;
		}
		return false;
	};

	const handleError = (
		errorMessage = 'Unable to enhance message. Please try again.'
	) => {
		console.error('AI Enhancement Error:', errorMessage);
		setLoading(false);
		setIsTyping(false);
		showAITypingIndicator('none');
		dismissThinkingToast();

		toast.error(errorMessage, {
			duration: 4000,
			position: toastPosition('bottom-right')
		});
	};

	const generateResponse = async () => {
		setLoading(true);
		showAITypingIndicator('thinking');
		showThinkingToast();

		try {
			const result = await props.generateAIResponse();

			if (result?.agreementRequired) {
				openAgreementDialog(result.agreementRequired, generateResponse);
				return;
			}

			const action = generatedResponseAction(result);

			if (action.kind === 'error') {
				handleError('Unable to generate response. Please try again.');
				return;
			}

			// The AI could not answer: nothing to type, only its note for the team.
			if (action.kind === 'note') {
				setLoading(false);
				showAITypingIndicator('none');
				dismissThinkingToast();
				showStaffNote("The AI couldn't write a reply", action.note);
				return;
			}

			const response = action.text;
			setLoading(false);
			showAITypingIndicator('typing');
			dismissThinkingToast();

			setIsTyping(true);
			setTypingText('');

			let currentText = '';
			for (let i = 0; i < response.length; i++) {
				await new Promise((resolve) => setTimeout(resolve, 8));
				currentText += response[i];
				setTypingText(currentText);
			}

			setIsTyping(false);
			showAITypingIndicator('none');
			props.updateMessageWithAI(response);
			if (action.note) showStaffNote('Note for your team', action.note);
		} catch (err) {
			if (handleAgreementError(err, generateResponse)) return;
			handleError('Unable to generate response. Please try again.');
		}
	};

	const expandText = async () => {
		setLoading(true);
		showAITypingIndicator('thinking');
		showThinkingToast();

		try {
			const res = await axios.post('/api/ai/expand-text', {
				text: props.message
			});

			const { result, error: apiError } = res.data;

			if (apiError) {
				handleError('Unable to expand text. Please try again.');
				return;
			}

			if (!result || typeof result !== 'string') {
				handleError('Invalid response from AI. Please try again.');
				return;
			}

			setLoading(false);
			showAITypingIndicator('typing');
			dismissThinkingToast();

			// Start typing animation
			setIsTyping(true);
			setTypingText('');

			// Type out the response character by character
			let currentText = '';
			for (let i = 0; i < result.length; i++) {
				await new Promise((resolve) => setTimeout(resolve, 8));
				currentText += result[i];
				setTypingText(currentText);
			}

			setIsTyping(false);
			showAITypingIndicator('none');
			props.updateMessageWithAI(result);
		} catch (err) {
			if (handleAgreementError(err, expandText)) return;
			handleError('Unable to expand text. Please try again.');
		}
	};

	const shortenText = async () => {
		setLoading(true);
		showAITypingIndicator('thinking');
		showThinkingToast();

		try {
			const res = await axios.post('/api/ai/shorten-text', {
				text: props.message
			});

			const { result, error: apiError } = res.data;

			if (apiError) {
				handleError('Unable to shorten text. Please try again.');
				return;
			}

			if (!result || typeof result !== 'string') {
				handleError('Invalid response from AI. Please try again.');
				return;
			}

			setLoading(false);
			showAITypingIndicator('typing');
			dismissThinkingToast();

			setIsTyping(true);
			setTypingText('');

			let currentText = '';
			for (let i = 0; i < result.length; i++) {
				await new Promise((resolve) => setTimeout(resolve, 8));
				currentText += result[i];
				setTypingText(currentText);
			}

			setIsTyping(false);
			showAITypingIndicator('none');
			props.updateMessageWithAI(result);
		} catch (err) {
			if (handleAgreementError(err, shortenText)) return;
			handleError('Unable to shorten text. Please try again.');
		}
	};

	const formalizeText = async () => {
		setLoading(true);
		showAITypingIndicator('thinking');
		showThinkingToast();

		try {
			const res = await axios.post('/api/ai/formalize-text', {
				text: props.message
			});

			const { result, error: apiError } = res.data;

			if (apiError) {
				handleError('Unable to formalize text. Please try again.');
				return;
			}

			if (!result || typeof result !== 'string') {
				handleError('Invalid response from AI. Please try again.');
				return;
			}

			setLoading(false);
			showAITypingIndicator('typing');
			dismissThinkingToast();

			// Start typing animation
			setIsTyping(true);
			setTypingText('');

			// Type out the response character by character
			let currentText = '';
			for (let i = 0; i < result.length; i++) {
				await new Promise((resolve) => setTimeout(resolve, 8));
				currentText += result[i];
				setTypingText(currentText);
			}

			setIsTyping(false);
			showAITypingIndicator('none');
			props.updateMessageWithAI(result);
		} catch (err) {
			if (handleAgreementError(err, formalizeText)) return;
			handleError('Unable to formalize text. Please try again.');
		}
	};

	const friendlyTone = async () => {
		setLoading(true);
		showAITypingIndicator('thinking');
		showThinkingToast();

		try {
			const res = await axios.post('/api/ai/friendly-tone', {
				text: props.message
			});

			const { result, error: apiError } = res.data;

			if (apiError) {
				handleError('Unable to make text friendly. Please try again.');
				return;
			}

			if (!result || typeof result !== 'string') {
				handleError('Invalid response from AI. Please try again.');
				return;
			}

			setLoading(false);
			showAITypingIndicator('typing');
			dismissThinkingToast();

			// Start typing animation
			setIsTyping(true);
			setTypingText('');

			// Type out the response character by character
			let currentText = '';
			for (let i = 0; i < result.length; i++) {
				await new Promise((resolve) => setTimeout(resolve, 8));
				currentText += result[i];
				setTypingText(currentText);
			}

			setIsTyping(false);
			showAITypingIndicator('none');
			props.updateMessageWithAI(result);
		} catch (err) {
			if (handleAgreementError(err, friendlyTone)) return;
			handleError('Unable to make text friendly. Please try again.');
		}
	};

	const spellCheckText = async () => {
		setLoading(true);
		showAITypingIndicator('thinking');
		showThinkingToast();

		try {
			const res = await axios.post('/api/ai/spell-check-text', {
				text: props.message
			});

			const { result, error: apiError } = res.data;

			if (apiError) {
				handleError('Unable to spell check. Please try again.');
				return;
			}

			if (!result || typeof result !== 'string') {
				handleError('Invalid response from AI. Please try again.');
				return;
			}

			setLoading(false);
			showAITypingIndicator('typing');
			dismissThinkingToast();

			// Start typing animation
			setIsTyping(true);
			setTypingText('');

			// Type out the response character by character
			let currentText = '';
			for (let i = 0; i < result.length; i++) {
				await new Promise((resolve) => setTimeout(resolve, 8));
				currentText += result[i];
				setTypingText(currentText);
			}

			setIsTyping(false);
			showAITypingIndicator('none');
			props.updateMessageWithAI(result);
		} catch (err) {
			if (handleAgreementError(err, spellCheckText)) return;
			handleError('Unable to spell check. Please try again.');
		}
	};

	const enableAiTextProduct = async () => {
		await axios.post('/api/billing/add-ai');
		await props.fetchUser();
	};

	const handleAgreedPopupDialog = async () => {
		setLoading(true);
		try {
			await axios.post('/api/legal/accept-agreements', {
				agreementKeys: ['ai_platform_use_agreement'],
				source: 'ai_text_enable_dialog',
			});
			await enableAiTextProduct();
			setAiEnableDialogOpen(false);
		} catch (err) {
			if (handleAgreementError(err, handleAgreedPopupDialog)) return;
			toast.error('Could not enable AI texting. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const renderPopupDialog = () => (
		<AIDialog
			handleAgreedPopupDialog={handleAgreedPopupDialog}
			saving={loading}
		/>
	);

	React.useImperativeHandle(ref, () => ({
		// generate | expand | shorten | friendly | formal | spellcheck
		run: (kind) => {
			const actions = {
				generate: generateResponse,
				expand: expandText,
				shorten: shortenText,
				friendly: friendlyTone,
				formal: formalizeText,
				spellcheck: spellCheckText
			};
			if (loading || !actions[kind]) return;
			actions[kind]();
		},
		openEnable: () => setAiEnableDialogOpen(true),
		isBusy: () => loading || isTyping
	}));

	const renderAIButton = () => {
		if (!props.products || props.hideTrigger) {
			return;
		}

		if (props.products && !props.products.includes('ai-text')) {
			return (
				<IconButton
					aria-label="ai"
					loading={loading}
					disabled={loading}
					toolTipTitle="Enhance Message with AI"
					onClick={() => setAiEnableDialogOpen(true)}
				>
					<AiZapIcon loading={loading} />
				</IconButton>
			);
		}

		// if the business has not signed up for AI
		// then get them to agree to the terms

		// if business has signed up for AI and
		// and they have the correct permission
		return (
			<DropdownMenu className="font-sans">
				<Tooltip delayDuration={200}>
					<TooltipTrigger asChild>
						<div>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									aria-label="ai"
									disabled={loading}
								>
									<AiZapIcon loading={loading} />
								</Button>
							</DropdownMenuTrigger>
						</div>
					</TooltipTrigger>
					<TooltipContent>Enhance Message with AI</TooltipContent>
				</Tooltip>
				<DropdownMenuContent>
					<Tooltip delayDuration={200}>
						<TooltipTrigger asChild>
							<div className="flex flex-col space-y-1 font-sans">
								<DropdownMenuItem
									onClick={() => {
										generateResponse();
									}}
								>
									<Pencil className="mr-2 h-4 w-4" />
									Generate Text
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => {
										expandText();
									}}
									disabled={!props.message}
								>
									<MessageCircleMore className="mr-2 h-4 w-4" />
									Expand Text
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => {
										shortenText();
									}}
									disabled={!props.message}
								>
									<Minimize2 className="mr-2 h-4 w-4" />
									Shorten Text
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => {
										friendlyTone();
									}}
									disabled={!props.message}
								>
									<Smile className="mr-2 h-4 w-4" />
									Friendly Tone
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => {
										formalizeText();
									}}
									disabled={!props.message}
								>
									<Handshake className="mr-2 h-4 w-4" />
									Formal Tone
								</DropdownMenuItem>
								<DropdownMenuItem
									disabled={!props.message}
									onClick={() => {
										spellCheckText();
									}}
								>
									<SpellCheck className="mr-2 h-4 w-4" />
									Spell Check
								</DropdownMenuItem>
							</div>
						</TooltipTrigger>
						{!props.message && (
							<TooltipContent className="font-sans">
								Please enter a message to use this feature
							</TooltipContent>
						)}
					</Tooltip>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	return (
		<div>
			<Dialog open={aiEnableDialogOpen} onOpenChange={setAiEnableDialogOpen}>
				{renderAIButton()}
				{renderPopupDialog()}
			</Dialog>
			<AgreementAcceptanceDialog
				open={agreementDialogOpen}
				onOpenChange={setAgreementDialogOpen}
				agreements={requiredAgreements}
				source="ai_text_action"
				title="AI Message Enhancement Agreement required"
				description="To use AI message enhancement, you must review and accept the following agreement."
				onAccepted={async () => {
					const retry = pendingRetryRef.current;
					pendingRetryRef.current = null;
					if (retry) {
						await retry();
					}
				}}
			/>
		</div>
	);
});
AIEnhanceButton.displayName = 'AIEnhanceButton';

export default AIEnhanceButton;
